import { useRef, useState } from "react";
import { jsonEq } from "../utils/fp/json-eq";
import { useEffectOnce } from "./state";

export function useStateUpdate<T>(
  getKey: () => T,
  onUpdate: (current: T, abortController: AbortController) => Promise<void>,
) {
  const key = getKey();

  const [forKey, setForKey] = useState<T>(key);
  const pendingKey = useRef<{ key: T; controller: AbortController } | null>({ key, controller: new AbortController() });

  function onInternalUpdate(currentT: T) {
    if (pendingKey.current?.controller) {
      pendingKey.current?.controller.abort();
    }

    const abortController = new AbortController();
    pendingKey.current = { key: currentT, controller: abortController };

    return onUpdate(currentT, abortController).finally(() => {
      pendingKey.current = null;
    });
  }

  useEffectOnce(() => {
    void onInternalUpdate(forKey);

    return true;
  }, []);

  const onPossibleKeyUpdate = (currentT: T) => {
    if (jsonEq(forKey, currentT)) {
      return;
    }

    setForKey(currentT);
    return onInternalUpdate(currentT);
  };

  void onPossibleKeyUpdate(key);

  return forKey;
}
